import "./styles.scss";

import moment from "moment-timezone";
import { FlagIcon } from "react-flag-kit";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, DialogTitle, DialogActions, DialogContent } from "@material-ui/core";

import Api from "../../../../services/api";
import Pagination from "../../../../components/Pagination";
import { decryptIfEncrypted } from "../../../../utils/fieldDecryptor";
import iconEditSVG from "../../../../assets/images/icons/icon_edit.svg";
import Placeholder from "../../../../components/Placeholder/PlaceholderByPage";
import { endLoading, initLoading } from "../../../../store/slices/generics.slice";
import { getWordoftheday, postWordoftheday, putWordoftheday } from "../../../../store/middleware";

const WordTheDay = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.userReducer);
  const { plans } = useSelector((state) => state.studentReducer);
  const { allLingos } = useSelector((state) => state.genericReducer);
  const { wordoftheday } = useSelector((state) => state.contentReducer);

  const [newPlans, setNewPlans] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [multiPlans, setMultiPlans] = useState(false);
  const [languagesMulti, setLanguagesMulti] = useState([]);
  const [buttonSelectLanguage, setButtonSelectLanguage] = useState("EN");

  const [pageNumber, setPageNumber] = useState(1);

  const [date, setDate] = useState("");
  const [basic, setBasic] = useState("");
  const [basicID, setBasicID] = useState("");
  const [advanced, setAdvanced] = useState("");
  const [basicLink, setBasicLink] = useState("");
  const [advancedID, setAdvancedID] = useState("");
  const [advancedLink, setAdvancedLink] = useState("");
  const [intermediary, setIntermediary] = useState("");
  const [intermediaryID, setIntermediaryID] = useState("");
  const [intermediaryLink, setIntermediaryLink] = useState("");

  const dateNow = moment().format("YYYY-MM-DD");
  const language = localStorage.getItem("i18nextLng");

  const isTeacher = user?.role === "teacher";
  const isStudent = user?.role === "student";
  const isCS = user?.role === "customerService";
  const isManager = user?.role === "companyManager";
  const languageFormatted = language === "en" ? "MM/DD/YYYY" : language === "es" ? "DD/MM/YYYY" : "DD/MM/YYYY";

  const customOrder = [1, 3, 2];
  const sortedItems = allLingos.filter((item) => customOrder.includes(item.id));
  const remainingItems = allLingos.filter((item) => !customOrder.includes(item.id));
  sortedItems.sort((a, b) => customOrder.indexOf(a.id) - customOrder.indexOf(b.id));
  const finalResultLanguage = [...sortedItems, ...remainingItems];

  const getDataWord = (lang = "EN", page = 1, date = "") => {
    dispatch(getWordoftheday(lang, page, date));
  };

  const resetFormsModal = () => {
    setDate("");
    setBasic("");
    setBasicID("");
    setAdvanced("");
    setBasicLink("");
    setAdvancedID("");
    setAdvancedLink("");
    setIntermediary("");
    setIntermediaryID("");
    setIntermediaryLink("");
  };

  const handleClickOpen = () => {
    resetFormsModal();

    try {
      var lastDate = moment(wordoftheday?.items[0]?.date);
      var lastDateAddOne = lastDate.add(1, "days");
      setDate(lastDateAddOne);
    } catch (e) {
      console.log(e);
    }

    setOpenModal(true);
  };

  const handleClickClose = () => setOpenModal(false);

  const handleAdd = () => {
    try {
      const content = [basic, intermediary, advanced];
      const id = [basicID, intermediaryID, advancedID];
      const level = ["basic", "intermediate", "advanced"];
      const externalLink = [basicLink, intermediaryLink, advancedLink];

      let body = {};

      for (let x = 0; x < 3; x++) {
        body = {
          level: level[x],
          content: content[x],
          textType: "WORDOFTHEDAY",
          externalLink: externalLink[x],
          language: buttonSelectLanguage,
          date: moment(date).format("YYYY-MM-DD"),
        };

        if (basicID) {
          let data = { ...body, id: id[x] };
          dispatch(putWordoftheday(data)).then(() => getDataWord(buttonSelectLanguage, pageNumber));
        } else {
          dispatch(postWordoftheday(body)).then(() => getDataWord(buttonSelectLanguage, pageNumber));
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      resetFormsModal();
      setOpenModal(false);
    }
  };

  const handleEdit = (item) => {
    setDate(item.date);
    setBasicID(item.words.find((item) => item.level === "basic")?.id);
    setBasic(item.words.find((item) => item.level === "basic")?.content);
    setAdvancedID(item.words.find((item) => item.level === "advanced")?.id);
    setAdvanced(item.words.find((item) => item.level === "advanced")?.content);
    setBasicLink(item.words.find((item) => item.level === "basic")?.externalLink);
    setIntermediaryID(item.words.find((item) => item.level === "intermediate")?.id);
    setAdvancedLink(item.words.find((item) => item.level === "advanced")?.externalLink);
    setIntermediary(item.words.find((item) => item.level === "intermediate")?.content);
    setIntermediaryLink(item.words.find((item) => item.level === "intermediate")?.externalLink);
    setOpenModal(true);
  };

  const handlePagination = (page) => {
    if (page === pageNumber) return;
    setPageNumber(page);

    if (isTeacher || isStudent || isCS) {
      getDataWord(buttonSelectLanguage, page, dateNow);
    }
    getDataWord(buttonSelectLanguage, page);
  };

  useEffect(() => {
    if (isStudent) {
      const newPlan = plans?.flatMap((item) =>
        item.studentPlanLanguages.map((lang) => decryptIfEncrypted(lang.lingoLanguage?.language?.name).toUpperCase()),
      );
      const lang =
        newPlan[0] === "ENGLISH" ? "EN" : newPlan[0] === "SPANISH" ? "ES" : newPlan[0] === "PORTUGUESE" ? "PT" : "FR";

      if (plans[0]?.plan.multiLingo) {
        let languages = [];
        plans?.map((plan) => {
          plan?.studentPlanLanguages?.map((language) => {
            languages.push(language?.lingoLanguage);
          });
        });

        setMultiPlans(true);
        setLanguagesMulti(languages);
        setButtonSelectLanguage("EN");
        getDataWord("EN", pageNumber, dateNow);
      } else {
        if (newPlan.length > 1) {
          setNewPlans(newPlan);

          if (newPlan?.includes("ENGLISH")) {
            setButtonSelectLanguage("EN");
            getDataWord("EN", pageNumber, dateNow);
          } else if (newPlan?.includes("PORTUGUESE")) {
            setButtonSelectLanguage("PT");
            getDataWord("PT", pageNumber, dateNow);
          } else if (newPlan?.includes("FRANCE")) {
            setButtonSelectLanguage("FR");
            getDataWord("FR", pageNumber, dateNow);
          } else {
            setButtonSelectLanguage("ES");
            getDataWord("ES", pageNumber, dateNow);
          }
        } else {
          setNewPlans(newPlan);
          setButtonSelectLanguage(lang);
          getDataWord(lang, pageNumber, dateNow);
        }
      }
    }

    if (isTeacher) {
      dispatch(initLoading());
      Api.get(`teachermanagement/getbyuser/${user?.id}`)
        .then((res) => {
          const plans = res?.data?.result?.items[0]?.teachers[0]?.teacherLanguages
            .filter((teacherLanguage) => teacherLanguage.isNative)
            .map((teacherLanguageFilter) => decryptIfEncrypted(teacherLanguageFilter?.language?.name).toUpperCase());

          const lang =
            plans[0] === "ENGLISH" ? "EN" : plans[0] === "SPANISH" ? "ES" : plans[0] === "PORTUGUESE" ? "PT" : "FR";

          setNewPlans(plans);
          setButtonSelectLanguage(lang);
          getDataWord(lang, pageNumber, dateNow);
        })
        .catch((err) => console.log("teachermanagement ", err))
        .finally(() => dispatch(endLoading()));
    }

    if (isCS) getDataWord("EN", pageNumber, dateNow);
    if (isManager) getDataWord(buttonSelectLanguage);
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div className="containerWord">
        {(isStudent || isTeacher) && (
          <div className="selectLanguage">
            {multiPlans ? (
              <div className="language">
                {languagesMulti
                  .filter((item) => item.active)
                  .sort((a, b) => a.id - b.id)
                  .map((item, i) => {
                    let valueFormatted =
                      item.flag === "US" ? "EN" : item.flag === "BR" ? "PT" : item.flag === "ES" ? "ES" : item.flag;

                    return (
                      <div
                        key={i}
                        onClick={() => {
                          setButtonSelectLanguage(valueFormatted);
                          getDataWord(valueFormatted, pageNumber, dateNow);
                        }}
                        className={buttonSelectLanguage === valueFormatted ? "active" : ""}
                      >
                        <FlagIcon size={35} code={item.flag} />
                        <span className="titleLanguage">{t(item.description.toUpperCase())}</span>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="language">
                {newPlans.includes("ENGLISH") && (
                  <div
                    onClick={() => {
                      setButtonSelectLanguage("EN");
                      getDataWord("EN", pageNumber, dateNow);
                    }}
                    className={buttonSelectLanguage === "EN" ? "active" : ""}
                  >
                    <FlagIcon size={35} code={"US"} />
                    <span className="titleLanguage">{t("BTN_ENGLISH")}</span>
                  </div>
                )}

                {newPlans.includes("SPANISH") && (
                  <div
                    onClick={() => {
                      setButtonSelectLanguage("ES");
                      getDataWord("ES", pageNumber, dateNow);
                    }}
                    className={buttonSelectLanguage === "ES" ? "active" : ""}
                  >
                    <FlagIcon size={35} code={"ES"} />
                    <span className="titleLanguage">{t("BTN_SPANISH")}</span>
                  </div>
                )}

                {newPlans.includes("PORTUGUESE") && (
                  <div
                    onClick={() => {
                      setButtonSelectLanguage("PT");
                      getDataWord("PT", pageNumber, dateNow);
                    }}
                    className={buttonSelectLanguage === "PT" ? "active" : ""}
                  >
                    <FlagIcon size={35} code={"BR"} />
                    <span className="titleLanguage">{t("BTN_PORTUGUESE")}</span>
                  </div>
                )}

                {newPlans.includes("FRENCH") && (
                  <div
                    onClick={() => {
                      setButtonSelectLanguage("FR");
                      getDataWord("FR", pageNumber, dateNow);
                    }}
                    className={buttonSelectLanguage === "FR" ? "active" : ""}
                  >
                    <FlagIcon size={35} code={"FR"} />
                    <span className="titleLanguage">{t("FRENCH")}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {(isManager || isCS) && (
          <div className="selectLanguage">
            <div className="language">
              {finalResultLanguage?.map((item, index) => {
                let flag = item.flag === "BR" ? "PT" : item.flag === "US" ? "EN" : item.flag;
                return (
                  <div
                    key={index}
                    className={buttonSelectLanguage === flag ? "active" : ""}
                    onClick={() => {
                      setButtonSelectLanguage(flag);
                      isCS === true ? getDataWord(flag, pageNumber, dateNow) : getDataWord(flag);
                    }}
                  >
                    <FlagIcon size={35} code={item.flag} />
                    <span className="titleLanguage">{item.description}</span>
                  </div>
                );
              })}
            </div>

            {isManager && (
              <button onClick={handleClickOpen} className="addContent">
                {t("BTN_ADD")}
              </button>
            )}
          </div>
        )}

        <div className="containerTableWord">
          {wordoftheday?.items?.length > 0 ? (
            <>
              <table>
                <thead>
                  <tr>
                    <th>{t("DATE")} - (EST)</th>
                    <th>{t("BASIC")}</th>
                    <th>{t("LINGO_INTER")}</th>
                    <th>{t("LINGO_ADV")}</th>
                    {isManager && <th>{t("OPTIONS")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {wordoftheday?.items?.map((item, i) => {
                    return (
                      <tr key={i}>
                        <td className="center styleDate">{moment(item.date).format(languageFormatted)}</td>
                        <td>
                          <a
                            target="_blank"
                            rel="noreferrer"
                            className="wrapperButton"
                            href={item.words.find((item) => item.level === "basic")?.externalLink}
                          >
                            <span className="buttonWord green">
                              {item.words.find((item) => item.level === "basic")?.content}
                            </span>
                          </a>
                        </td>
                        <td>
                          <a
                            target="_blank"
                            rel="noreferrer"
                            className="wrapperButton"
                            href={item.words.find((item) => item.level === "intermediate")?.externalLink}
                          >
                            <span className="buttonWord yellow">
                              {item.words.find((item) => item.level === "intermediate")?.content}
                            </span>
                          </a>
                        </td>
                        <td>
                          <a
                            target="_blank"
                            rel="noreferrer"
                            className="wrapperButton"
                            href={item.words.find((item) => item.level === "advanced")?.externalLink}
                          >
                            <span className="buttonWord red">
                              {item.words.find((item) => item.level === "advanced")?.content}
                            </span>
                          </a>
                        </td>
                        {isManager && (
                          <td className="center">
                            <div className="wrapperButton">
                              <button onClick={() => handleEdit(item)}>
                                <img src={iconEditSVG} alt="Edit Icon" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {wordoftheday?.items?.map((item) => (
                <div key={item?.date} className="tableResponsive">
                  <div>
                    <p className="title">{t("DATE")} - EST</p>
                    <p>{moment(item.date).format(languageFormatted)}</p>
                  </div>
                  <div>
                    <p className="title">{t("BASIC")}</p>
                    <a
                      target="_blank"
                      rel="noreferrer"
                      className="wrapperButton"
                      href={item.words.find((item) => item.level === "basic")?.externalLink}
                    >
                      <span className="buttonWord green">
                        {item.words.find((item) => item.level === "basic")?.content}
                      </span>
                    </a>
                  </div>
                  <div>
                    <p className="title">{t("LINGO_INTER")}</p>
                    <a
                      target="_blank"
                      rel="noreferrer"
                      className="wrapperButton"
                      href={item.words.find((item) => item.level === "intermediate")?.externalLink}
                    >
                      <span className="buttonWord yellow">
                        {item.words.find((item) => item.level === "intermediate")?.content}
                      </span>
                    </a>
                  </div>
                  <div>
                    <p className="title">{t("LINGO_ADV")}</p>
                    <a
                      target="_blank"
                      rel="noreferrer"
                      className="wrapperButton"
                      href={item.words.find((item) => item.level === "advanced")?.externalLink}
                    >
                      <span className="buttonWord red">
                        {item.words.find((item) => item.level === "advanced")?.content}
                      </span>
                    </a>
                  </div>
                  {isManager && (
                    <div>
                      <p className="title">{t("OPTIONS")}</p>
                      <div className="wrapperButton">
                        <button onClick={() => handleEdit(item)}>
                          <img src={iconEditSVG} alt="Edit Icon" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : (
            <div style={{ marginTop: 40 }}>
              <Placeholder pageName="users" textMsg="Nothing here yet" />
            </div>
          )}

          <Pagination
            pageCurrent={pageNumber}
            totalFound={wordoftheday?.totalCount}
            totalPages={wordoftheday?.others?.totalPages}
            totalResults={wordoftheday?.others?.totalResults}
            onClick={(page, type) => handlePagination(page, type)}
          />
        </div>

        <Dialog
          open={openModal}
          onClose={handleClickClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            <span className="titleModal">Nova palavra do dia</span>
          </DialogTitle>
          <DialogContent>
            <div className="formModal">
              <div className="row">
                <div className="divInput" style={{ width: "100%" }}>
                  <label className="labelModal">{t("DATE")}</label>
                  <input
                    disabled
                    onChange={(e) => setDate(e.target.value)}
                    value={moment(date).format(languageFormatted)}
                  />
                </div>
              </div>

              <div className="row">
                <div className="divInput" style={{ width: "30%" }}>
                  <label className="labelModal">{t("BASIC")}</label>
                  <input required value={basic} onChange={(e) => setBasic(e.target.value)} />
                </div>
                <div className="divInput" style={{ width: "60%" }}>
                  <label className="labelModal">{`Link ${t("BASIC")}`}</label>
                  <input required value={basicLink} onChange={(e) => setBasicLink(e.target.value)} />
                </div>
              </div>

              <div className="row">
                <div className="divInput" style={{ width: "30%" }}>
                  <label className="labelModal">{t("LINGO_INTER")}</label>
                  <input required value={intermediary} onChange={(e) => setIntermediary(e.target.value)} />
                </div>
                <div className="divInput" style={{ width: "60%" }}>
                  <label className="labelModal">{`Link ${t("LINGO_INTER")}`}</label>
                  <input required value={intermediaryLink} onChange={(e) => setIntermediaryLink(e.target.value)} />
                </div>
              </div>

              <div className="row">
                <div className="divInput" style={{ width: "30%" }}>
                  <label className="labelModal">{t("LINGO_ADV")}</label>
                  <input required value={advanced} onChange={(e) => setAdvanced(e.target.value)} />
                </div>
                <div className="divInput" style={{ width: "60%" }}>
                  <label className="labelModal">{`Link ${t("LINGO_ADV")}`}</label>
                  <input required value={advancedLink} onChange={(e) => setAdvancedLink(e.target.value)} />
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <button
              type="submit"
              className="addContent"
              onClick={handleAdd}
              style={{ marginBottom: 10, marginRight: 20, paddingLeft: 20, paddingRight: 20 }}
            >
              {!basicID ? t("BTN_ADD") : t("CHANGE")}
            </button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default WordTheDay;
